import {
    Box,
    IconButton,
    List,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Skeleton,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import { Styled } from "../styles";
import { Organization } from "@/types/utils";
import { Brand, Product } from "@/types/entities";
import { StepFormValues } from "../../..";
import { useState } from "react";
import { Close } from "@dippi/icons";

interface Props {
    organizations: Organization[] | undefined;
    brands: Brand[] | undefined;
    products: Product[] | undefined;
}

const ParticipantsList: React.FC<Props> = ({ organizations, brands, products }) => {
    const [isOrganizationOpen, setIsOrganizationOpen] = useState<string | null>(null);
    const [isBrandOpen, setIsBrandOpen] = useState<string | null>(null);
    const { watch, setValue } = useFormContext<StepFormValues>();
    const participants = watch("participants") as Record<string, Record<string, string[]>>;

    const onProductRemove = (
        orgId: string,
        brandsMap: Record<string, string[]>,
        brandId: string,
        productIds: string[],
        productId: string,
    ) => {
        const updatedParticipants: Record<string, Record<string, string[]>> = {
            ...participants,
            [orgId]: {
                ...(brandsMap as Record<string, string[]>),
                [brandId]: productIds.filter(id => id !== productId),
            },
        };

        setValue("participants", updatedParticipants, { shouldDirty: true });
    };

    return (
        <Styled.ListContainer sx={{ minWidth: 400 }}>
            {Object.entries(participants).map(([orgId, brandsMap]) => {
                const organization = organizations?.find(org => org._id === orgId);
                const isCurrentOrganizationOpen = isOrganizationOpen === orgId;
                return (
                    <Styled.ProductsList key={orgId}>
                        <div>
                            <Box sx={{ padding: "16px 0" }}>
                                <Styled.Border />
                            </Box>
                            <ListSubheader
                                onClick={() => {
                                    setIsOrganizationOpen(prev => (prev === orgId ? null : orgId));
                                    setValue("selectedOrganizationId", orgId);
                                }}
                                sx={{ cursor: "pointer" }}
                            >
                                {organization?.title || (
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        sx={{ fontSize: 16 }}
                                    />
                                )}
                            </ListSubheader>
                            {isCurrentOrganizationOpen && (
                                <Styled.BrandsList>
                                    {Object.entries(brandsMap as Record<string, string[]>).map(
                                        ([brandId, productIds]) => {
                                            const brand = brands?.find(b => b._id === brandId);
                                            const isCurrentBrandOpen = isBrandOpen === brandId;
                                            return (
                                                <div key={brandId}>
                                                    <ListSubheader
                                                        onClick={() => {
                                                            setIsBrandOpen(prev =>
                                                                prev === brandId ? null : brandId,
                                                            );
                                                            setValue("selectedBrandId", brandId);
                                                        }}
                                                        sx={{
                                                            cursor: "pointer",
                                                            paddingLeft: "16px",
                                                        }}
                                                    >
                                                        {brand?.title || (
                                                            <Skeleton
                                                                variant="text"
                                                                animation="wave"
                                                                sx={{ fontSize: 14 }}
                                                            />
                                                        )}
                                                    </ListSubheader>
                                                    {isCurrentBrandOpen && (
                                                        <List>
                                                            {productIds.map(productId => {
                                                                const product = products?.find(
                                                                    p => p._id === productId,
                                                                );
                                                                return (
                                                                    <Styled.ParticipantProducts
                                                                        key={productId}
                                                                    >
                                                                        <ListItemText
                                                                            primary={
                                                                                product?.longTitle || (
                                                                                    <Skeleton
                                                                                        variant="text"
                                                                                        animation="wave"
                                                                                        sx={{
                                                                                            fontSize: 14,
                                                                                        }}
                                                                                    />
                                                                                )
                                                                            }
                                                                            primaryTypographyProps={{
                                                                                fontSize: "14px",
                                                                            }}
                                                                        />
                                                                        <ListItemIcon>
                                                                            <IconButton
                                                                                edge="end"
                                                                                color="error"
                                                                                onClick={() =>
                                                                                    onProductRemove(
                                                                                        orgId,
                                                                                        brandsMap,
                                                                                        brandId,
                                                                                        productIds,
                                                                                        productId,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <Close />
                                                                            </IconButton>
                                                                        </ListItemIcon>
                                                                    </Styled.ParticipantProducts>
                                                                );
                                                            })}
                                                        </List>
                                                    )}
                                                </div>
                                            );
                                        },
                                    )}
                                </Styled.BrandsList>
                            )}
                        </div>
                    </Styled.ProductsList>
                );
            })}
        </Styled.ListContainer>
    );
};

export default ParticipantsList;
